import { alertController } from '@ionic/vue'

interface CustomAlertOptions {
  header?: string
  message: string
  confirmText?: string
  cancelText?: string
  onConfirm?: () => void | Promise<void>
  onCancel?: () => void | Promise<void>
  showCancel?: boolean
  cssClass?: string
}

export const useCustomAlert = () => {
  const showAlert = async (options: CustomAlertOptions) => {
    const {
      header,
      message,
      confirmText = 'OK',
      cancelText = 'Cancel',
      onConfirm,
      onCancel,
      showCancel = false,
      cssClass = 'custom-alert',
    } = options

    const buttons = []

    if (showCancel) {
      buttons.push({
        text: cancelText,
        role: 'cancel',
        handler: () => {
          if (onCancel) onCancel()
        },
      })
    }

    buttons.push({
      text: confirmText,
      role: 'confirm',
      handler: () => {
        if (onConfirm) onConfirm()
      },
    })

    const alert = await alertController.create({
      header,
      message,
      cssClass,
      buttons,
      backdropDismiss: false,
    })

    await alert.present()
    return alert
  }

  const showConfirmation = async (
    message: string,
    onConfirm?: () => void | Promise<void>,
    onCancel?: () => void | Promise<void>,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    header = 'Confirm',
  ) => {
    return showAlert({
      header,
      message,
      confirmText,
      cancelText,
      onConfirm,
      onCancel,
      showCancel: true,
    })
  }

  const showError = async (
    message: string,
    onConfirm?: () => void | Promise<void>,
  ) => {
    return showAlert({
      header: 'Error',
      message,
      onConfirm,
      cssClass: 'custom-alert custom-alert-error',
    })
  }

  const showSuccess = async (
    message: string,
    onConfirm?: () => void | Promise<void>,
  ) => {
    return showAlert({
      header: 'Success',
      message,
      onConfirm,
    })
  }

  const showWarning = async (
    message: string,
    onConfirm?: () => void | Promise<void>,
    onCancel?: () => void | Promise<void>,
  ) => {
    return showAlert({
      header: 'Warning',
      message,
      confirmText: 'OK',
      onConfirm,
      onCancel,
      showCancel: !!onCancel,
    })
  }

  const showMultipleSelectAlert = async (options: CustomAlertOptions) => {
    return showAlert({
      ...options,
      cssClass: 'custom-alert multiple-select-alert',
    })
  }

  return {
    showAlert,
    showConfirmation,
    showError,
    showSuccess,
    showWarning,
    showMultipleSelectAlert,
  }
}
